import { type Translation } from "@bimproject/t9n";
import { useEffect, useState } from "react";
import styled from "styled-components";

import {
    fetchData,
    saveData,
    type ApplicationData,
    type DataProperty,
    type DataTexts,
} from "../../../../clients/documentExports/api";
import useNotifications from "../../../../components/Notifications";
import * as UI from "../../../../modules/ui-kit";

const Styled = styled.section`
    display: flex;
    flex-direction: column;
    gap: 16px;

    h3 {
        margin: 0;
    }

    .property {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .actions {
        align-items: center;
        display: flex;
        gap: 12px;

        .state {
            color: #889;
            font-size: .9em;
        }
    }
`;

const toTranslations = (texts: DataTexts = {}): Translation[] => Object.entries(texts).map(([language, text]) => ({ language, text }));
const toTexts = (translations: Translation[]): DataTexts => Object.fromEntries(translations.map(({ language, text }) => [language, text]));
const same = (a: DataTexts, b: DataTexts) => JSON.stringify(Object.entries(a).sort()) === JSON.stringify(Object.entries(b).sort());

// The application's texts the service fills into every one of its templates, per
// language (data.json): the disclaimer today, more later. Each property is its own
// form with its own save, so one save is one atomic replace on the service and every
// changed language one history entry. The properties come from the service listing.
export default function ApplicationTexts({ applicationDomain, properties }: { applicationDomain: string, properties: DataProperty[] }) {
    const notifications = useNotifications();
    const [stored, setStored] = useState<ApplicationData | null>(null);

    useEffect(
        () => {
            let current = true;
            setStored(null);
            fetchData(applicationDomain)
                .then((data) => { if (current) setStored(data); })
                .catch((error) => notifications.error(`Could not load the texts: ${ (error as Error).message }`));
            return () => { current = false; };
        },
        [applicationDomain, notifications],
    );

    if (!properties.length) return null;

    return (
        <Styled>
            <h3>General placeholders for templates</h3>
            { stored === null && <span>loading</span> }
            { stored !== null && properties.map((property) => (
                <PropertyEditor
                    applicationDomain={ applicationDomain }
                    key={ `${ applicationDomain }:${ property.value }` }
                    property={ property }
                    stored={ stored[property.value] ?? {} }
                    onStored={ (texts) => setStored({ ...stored, [property.value]: texts }) }
                />
            )) }
        </Styled>
    );
}

function PropertyEditor(props: { applicationDomain: string, property: DataProperty, stored: DataTexts, onStored: (texts: DataTexts) => void }) {
    const { applicationDomain, property, stored, onStored } = props;
    const notifications = useNotifications();
    const [draft, setDraft] = useState<Translation[]>(() => toTranslations(stored));
    const [busy, setBusy] = useState(false);
    const dirty = !same(toTexts(draft), stored);

    async function save() {
        setBusy(true);
        try {
            const outcome = await saveData(applicationDomain, property.value, toTexts(draft));
            if (outcome.ok && outcome.data) {
                onStored(outcome.data);
                setDraft(toTranslations(outcome.data));
                const changed = outcome.changed ?? [];
                notifications.success(changed.length ? `${ property.label } saved (${ changed.join(", ") })` : `${ property.label }: nothing changed`);
            } else {
                notifications.error(outcome.errors?.[0] ?? `Could not save ${ property.label }`);
            }
        } catch (error) {
            notifications.error(`Could not save ${ property.label }: ${ (error as Error).message }`);
        } finally {
            setBusy(false);
        }
    }

    return (
        <UI.Form className="property" onSubmit={ (event) => { event.preventDefault(); save(); } }>
            <UI.FieldLabel>{ property.label }</UI.FieldLabel>
            <UI.TranslatedInput
                disabled={ busy }
                label={ property.label.toLowerCase() }
                onChange={ setDraft }
                value={ draft }
            />
            <div className="actions">
                <UI.Button $appearance="primary" disabled={ busy || !dirty } type="submit">
                    { busy ? "saving…" : `save ${ property.label.toLowerCase() }` }
                </UI.Button>
                <span className="state">{ dirty ? "unsaved changes" : "saved" }</span>
            </div>
        </UI.Form>
    );
}
